import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  Navigate,
  useNavigate,
} from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { LoginPage } from '@/features/login/components/login-page';
import { RecoveryPage } from '@/features/password-recovery/components/recovery-page';
import { MfaPage } from '@/features/mfa/components/mfa-page';
import { AcceptInvitePage } from '@/features/accept-invite/components/accept-invite-page';
import { IdpCallbackPage } from '@/features/idp-callback/components/idp-callback-page';
import { LogoutPage } from '@/features/logout/components/logout-page';
import { getAuthRequest } from '@/shared/lib/zitadel-client';
import { authRequestIdDe, parseSearch, stringifySearch } from '@/shared/lib/search-params';

// ---------------------------------------------------------------------------
// Env
// ---------------------------------------------------------------------------

const ZITADEL_AUTHORITY = import.meta.env.VITE_ZITADEL_AUTHORITY ?? 'http://localhost:8088';
// Client ID do app principal. Se não configurado, o login customizado é
// exibido para todos os auth requests (comportamento de dev sem restrição).
const MAIN_APP_CLIENT_ID: string | undefined = import.meta.env.VITE_ZITADEL_CLIENT_ID || undefined;

// ---------------------------------------------------------------------------
// Root
// ---------------------------------------------------------------------------

const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

// ---------------------------------------------------------------------------
// /login — tela principal de autenticação
// ---------------------------------------------------------------------------

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: (search: Record<string, unknown>) => ({
    authRequestId: authRequestIdDe(search),
  }),
  component: LoginPage,
});

// ---------------------------------------------------------------------------
// /recovery — recuperação de senha
// ---------------------------------------------------------------------------

const recoveryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/recovery',
  component: RecoveryPage,
});

// ---------------------------------------------------------------------------
// /mfa — verificação TOTP
// ---------------------------------------------------------------------------

const mfaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/mfa',
  component: MfaPage,
});

// ---------------------------------------------------------------------------
// /idp-callback — retorno do Identity Provider externo (Microsoft, etc.)
// ---------------------------------------------------------------------------

const idpCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/idp-callback',
  validateSearch: (search: Record<string, unknown>) => ({
    id: typeof search.id === 'string' ? search.id : undefined,
    token: typeof search.token === 'string' ? search.token : undefined,
    // userId do Zitadel quando o usuário externo já está vinculado (snowflake;
    // preservado como string pelo parseSearch custom — ver search-params.ts).
    user: typeof search.user === 'string' ? search.user : undefined,
    authRequestId: authRequestIdDe(search),
  }),
  component: IdpCallbackPage,
});

// ---------------------------------------------------------------------------
// /accept-invite — convidado clica no magic link recebido por email
// ---------------------------------------------------------------------------

const acceptInviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/accept-invite',
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === 'string' ? search.token : undefined,
  }),
  component: AcceptInvitePage,
});

// ---------------------------------------------------------------------------
// /logout — última etapa do end_session (Login V2)
//
// O Zitadel manda o browser pra cá (ZITADEL_OIDC_DEFAULTLOGOUTURLV2) depois de
// encerrar a sessão, com `?post_logout_redirect=<uri>`. Sem esta rota o usuário
// parava no login e nunca voltava ao app. A página lê a query direto do
// `window.location` (destino validado em post-logout-destino.ts).
// ---------------------------------------------------------------------------

const logoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/logout',
  component: LogoutPage,
});

// ---------------------------------------------------------------------------
// / — ponto de entrada: detecta o cliente e roteia adequadamente
//
// Lógica:
//  1. Se não há authRequestId → vai para /login (mostrará erro de param ausente)
//  2. Busca as infos do auth request no Zitadel (/v2/oidc/auth_requests/{id})
//  3. Se o clientId bate com VITE_ZITADEL_CLIENT_ID → exibe UI customizada
//  4. Se o clientId é de outro app (ex: console Zitadel) → redireciona
//     silenciosamente para o Login V1 nativo do Zitadel
//  5. Se a chamada falhar (sem PAT ou timeout) → exibe UI customizada por
//     segurança (fail-open para o app principal)
// ---------------------------------------------------------------------------

function IndexGate() {
  const navigate = useNavigate();
  const authRequestId = authRequestIdDe(parseSearch(window.location.search)) ?? null;

  type State = 'checking' | 'custom' | 'redirect_v1';
  const [state, setState] = useState<State>(authRequestId ? 'checking' : 'custom');

  useEffect(() => {
    if (!authRequestId) return;

    // Se não há MAIN_APP_CLIENT_ID configurado, exibe UI customizada diretamente
    if (!MAIN_APP_CLIENT_ID) {
      void navigate({ to: '/login', search: { authRequestId } });
      return;
    }

    getAuthRequest(authRequestId)
      .then((info) => {
        if (info.clientId && info.clientId !== MAIN_APP_CLIENT_ID) {
          // Auth request de outro app (console, etc.) → Login V1
          setState('redirect_v1');
        } else {
          // Nosso app ou clientId não disponível → UI customizada
          setState('custom');
        }
      })
      .catch(() => {
        // Falha na checagem → fail-open: exibe UI customizada
        setState('custom');
      });
  }, [authRequestId, navigate]);

  // Redireciona para Login V1 do Zitadel (tela nativa de admin)
  if (state === 'redirect_v1' && authRequestId) {
    window.location.assign(
      `${ZITADEL_AUTHORITY}/ui/login/login?authRequestID=${authRequestId}`
    );
    return <p className="p-8 text-sm text-ink-4">Redirecionando…</p>;
  }

  // Aguarda a verificação
  if (state === 'checking') {
    return <p className="p-8 text-sm text-ink-4">Carregando…</p>;
  }

  // UI customizada — navega para /login preservando o authRequestId
  const to = authRequestId ? `/login?authRequestId=${authRequestId}` : '/login';
  return <Navigate to={to} />;
}

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: IndexGate,
});

// ---------------------------------------------------------------------------
// Router
// ---------------------------------------------------------------------------

export const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  recoveryRoute,
  mfaRoute,
  acceptInviteRoute,
  idpCallbackRoute,
  logoutRoute,
]);

// `parseSearch`/`stringifySearch` customizados preservam strings: os IDs do
// Zitadel são snowflakes que estouram Number.MAX_SAFE_INTEGER e o JSON.parse
// padrão do TanStack os corromperia (ver search-params.ts).
export const router = createRouter({ routeTree, parseSearch, stringifySearch });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
